// =============================================================================
// Acme Corp Demo — Data & Scenarios
// =============================================================================

export interface DemoQuestion {
  id: string;
  category: string;
  question: string;
  frameworkTag?: string;
  context?: string;
  isFollowUp?: boolean;
  /** Shown as already answered before the demo starts */
  preAnswered?: boolean;
  preAnswer?: string;
}

export interface DemoEvent {
  timestamp: number;
  type:
    | 'answer_question'
    | 'ai_note'
    | 'press_harder'
    | 'suggest_followup'
    | 'transcript_line';
  questionId?: string;
  answer?: string;
  note?: { category: string; content: string; question?: string };
  criterion?: string;
  message?: string;
  followUpQuestion?: string;
  followUpContext?: string;
  speaker?: string;
  text?: string;
}

export interface DemoScenario {
  id: string;
  title: string;
  description: string;
  criterion: string;
  criterionName: string;
  videoSrc: string;
  /** The question ID that gets answered live (so the page scrolls to it) */
  liveQuestionId: string;
  questions: DemoQuestion[];
  categoryOrder: string[];
  events: DemoEvent[];
}

// =============================================================================
// Shared Acme Corp questions — full vendor call question set
// =============================================================================

const ACME_QUESTIONS_LEARN: DemoQuestion[] = [
  // --- AI Transparency (SEE) — pre-answered in LEARN demo (earlier in call) ---
  {
    id: 'see-1',
    category: 'AI Transparency',
    question: 'Can users toggle between models?',
    frameworkTag: 'SEE',
    context: 'Evaluate whether customers have direct control over which AI model powers different workflows.',
    preAnswered: true,
    preAnswer: 'Users can toggle between models. Agents can be configured to use specific models at the whole agent level, or per step within a workflow.',
  },
  {
    id: 'see-2',
    category: 'AI Transparency',
    question: 'Are system prompts visible and editable?',
    frameworkTag: 'SEE',
    context: 'Critical for understanding what instructions the AI is following behind the scenes.',
    preAnswered: true,
    preAnswer: 'Admins have full access to system prompts and can modify them within guardrails. End users can view agent setup and see thinking steps.',
  },
  {
    id: 'see-3',
    category: 'AI Transparency',
    question: 'Are there Acme Corp-level instructions that no customer can see or override?',
    frameworkTag: 'SEE',
    preAnswered: true,
    preAnswer: 'There are Acme-only baseline instructions covering safety behaviors, data handling, and brand guardrails that no customer admin can touch.',
  },

  // --- Transferable Knowledge (LEARN) — LIVE during demo ---
  {
    id: 'learn-q1',
    category: 'Transferable Knowledge',
    question: 'Can employees learn from and understand how the AI works?',
    frameworkTag: 'LEARN',
    context: 'LEARN criterion — assess whether using the tool builds genuine AI skills or just tool-specific muscle memory.',
  },
  {
    id: 'learn-followup',
    category: 'Transferable Knowledge',
    question: 'If an employee becomes a power user of Acme Corp, do those skills transfer if they move to a different platform? Are they building transferable AI skills, or just learning to use your tool?',
    frameworkTag: 'LEARN',
    isFollowUp: true,
  },
  {
    id: 'learn-3',
    category: 'Transferable Knowledge',
    question: 'What training is included with the platform?',
    frameworkTag: 'LEARN',
  },
  {
    id: 'learn-4',
    category: 'Transferable Knowledge',
    question: 'Do you offer training that builds generalizable AI skills — prompt engineering, agent design thinking, workflow decomposition?',
    frameworkTag: 'LEARN',
  },

  // --- Data & Portability (EXIT) — unanswered in LEARN demo ---
  {
    id: 'exit-q1',
    category: 'Data & Portability',
    question: "What happens to our company's data if we stop using Acme Corp's products?",
    frameworkTag: 'EXIT',
    context: 'EXIT criterion — understand real switching costs before committing.',
  },
  {
    id: 'exit-2',
    category: 'Data & Portability',
    question: 'Can the knowledge graph relationships be exported in any standard format?',
    frameworkTag: 'EXIT',
  },
  {
    id: 'exit-3',
    category: 'Data & Portability',
    question: 'Is data portability on your product roadmap?',
    frameworkTag: 'EXIT',
  },

  // --- Customization (CHANGE) — unanswered ---
  {
    id: 'change-1',
    category: 'Customization',
    question: 'Can different departments or teams have different AI behavior configurations?',
    frameworkTag: 'CHANGE',
  },
  {
    id: 'change-2',
    category: 'Customization',
    question: 'Is there an API for programmatic customization of search behavior and ranking?',
    frameworkTag: 'CHANGE',
  },

  // --- Commercial — unanswered ---
  {
    id: 'comm-1',
    category: 'Commercial',
    question: 'What is your pricing model per seat, and are there volume discounts above 500 users?',
  },
  {
    id: 'comm-2',
    category: 'Commercial',
    question: 'What are the contract termination terms and notice periods?',
  },
];

const ACME_QUESTIONS_EXIT: DemoQuestion[] = [
  // --- AI Transparency (SEE) — pre-answered ---
  {
    id: 'see-1',
    category: 'AI Transparency',
    question: 'Can users toggle between models?',
    frameworkTag: 'SEE',
    preAnswered: true,
    preAnswer: 'Users can toggle between models. Agents can be configured to use specific models at the whole agent level, or per step within a workflow.',
  },
  {
    id: 'see-2',
    category: 'AI Transparency',
    question: 'Are system prompts visible and editable?',
    frameworkTag: 'SEE',
    preAnswered: true,
    preAnswer: 'Admins have full access to system prompts and can modify them within guardrails. End users can view agent setup and see thinking steps.',
  },
  {
    id: 'see-3',
    category: 'AI Transparency',
    question: 'Are there Acme Corp-level instructions that no customer can see or override?',
    frameworkTag: 'SEE',
    preAnswered: true,
    preAnswer: 'There are Acme-only baseline instructions covering safety behaviors, data handling, and brand guardrails that no customer admin can touch.',
  },

  // --- Transferable Knowledge (LEARN) — pre-answered in EXIT demo ---
  {
    id: 'learn-q1',
    category: 'Transferable Knowledge',
    question: 'Can employees learn from and understand how the AI works?',
    frameworkTag: 'LEARN',
    preAnswered: true,
    preAnswer: 'Thinking steps are surfaced for every query. Users can inspect agent setup, view version history, and duplicate or remix agents.',
  },
  {
    id: 'learn-3',
    category: 'Transferable Knowledge',
    question: 'What training is included with the platform?',
    frameworkTag: 'LEARN',
    preAnswered: true,
    preAnswer: 'In-app tutorials and certifications live right inside the product. Many customers also set up internal admin labs for power users.',
  },
  {
    id: 'learn-4',
    category: 'Transferable Knowledge',
    question: 'Do you offer training that builds generalizable AI skills — prompt engineering, agent design thinking, workflow decomposition?',
    frameworkTag: 'LEARN',
    preAnswered: true,
    preAnswer: 'In-app certifications cover core skills. For broader skills like prompt engineering, our partner ecosystem delivers training — partners influence over 40% of revenue.',
  },

  // --- Data & Portability (EXIT) — LIVE during demo ---
  {
    id: 'exit-q1',
    category: 'Data & Portability',
    question: "What happens to our company's data if we stop using Acme Corp's products?",
    frameworkTag: 'EXIT',
    context: 'EXIT criterion — understand real switching costs before committing.',
  },
  {
    id: 'exit-followup',
    category: 'Data & Portability',
    question: 'What specifically could we export and use elsewhere? Can you tell me what format that would be in?',
    frameworkTag: 'EXIT',
    isFollowUp: true,
  },
  {
    id: 'exit-2',
    category: 'Data & Portability',
    question: 'Can the knowledge graph relationships be exported in any standard format?',
    frameworkTag: 'EXIT',
  },
  {
    id: 'exit-3',
    category: 'Data & Portability',
    question: 'Is data portability on your product roadmap?',
    frameworkTag: 'EXIT',
  },

  // --- Customization (CHANGE) — unanswered ---
  {
    id: 'change-1',
    category: 'Customization',
    question: 'Can different departments or teams have different AI behavior configurations?',
    frameworkTag: 'CHANGE',
  },
  {
    id: 'change-2',
    category: 'Customization',
    question: 'Is there an API for programmatic customization of search behavior and ranking?',
    frameworkTag: 'CHANGE',
  },

  // --- Commercial — unanswered ---
  {
    id: 'comm-1',
    category: 'Commercial',
    question: 'What is your pricing model per seat, and are there volume discounts above 500 users?',
  },
  {
    id: 'comm-2',
    category: 'Commercial',
    question: 'What are the contract termination terms and notice periods?',
  },
];

const CATEGORY_ORDER = [
  'AI Transparency',
  'Transferable Knowledge',
  'Data & Portability',
  'Customization',
  'Commercial',
];

// =============================================================================
// Q4 / LEARN Scenario
// =============================================================================

const LEARN_SCENARIO: DemoScenario = {
  id: 'learn',
  title: 'Transferable Knowledge',
  description: 'Can employees learn from and understand how the AI works?',
  criterion: 'LEARN',
  criterionName: 'Transferable Knowledge',
  videoSrc: '/demo/acme-q4-learn.mp4',
  liveQuestionId: 'learn-q1',
  questions: ACME_QUESTIONS_LEARN,
  categoryOrder: CATEGORY_ORDER,
  events: [
    {
      timestamp: 2,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: 'Could my employees learn from and understand how the AI works?',
    },
    {
      timestamp: 8,
      type: 'transcript_line',
      speaker: 'Erica',
      text: 'Yeah, so thinking steps are surfaced for every query. Users can inspect agent setup, view version history, and duplicate or remix agents. Admins see full logs and traces in their own cloud infrastructure.',
    },
    {
      timestamp: 12,
      type: 'answer_question',
      questionId: 'learn-q1',
      answer:
        'Thinking steps are surfaced for every query. Users can inspect agent setup, view version history, and duplicate or remix agents. Admins see full logs and traces in their own cloud infrastructure.',
    },
    {
      timestamp: 14,
      type: 'ai_note',
      note: {
        category: 'LEARN',
        question: 'Are they learning portable skills, or just learning your tool?',
        content:
          'The vendor described inspection features (thinking steps, agent setup, logs) but didn\'t address whether employees build transferable AI skills.',
      },
    },
    {
      timestamp: 19,
      type: 'press_harder',
      criterion: 'LEARN',
      message:
        "The vendor's answer focuses on inspection features, not on whether employees actually build transferable AI skills. Dig deeper.",
    },
    {
      timestamp: 25,
      type: 'suggest_followup',
      criterion: 'LEARN',
      followUpQuestion:
        'If an employee becomes a power user of Acme Corp, do those skills transfer if they move to a different platform? Are they building transferable AI skills, or just learning to use your tool?',
      followUpContext:
        'The answer described visibility into AI operations, but did not address whether employees develop portable skills.',
    },
    {
      timestamp: 23,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: 'Great. And let me just look at my notes here. Just to push a little bit more on that, if an employee became a power user of your tool. Do those skills they learn transfer if we move to a different platform? So are they building transferable AI skills, or just learning to use your tool?',
    },
    {
      timestamp: 41,
      type: 'transcript_line',
      speaker: 'Erica',
      text: 'Love that question. Agent design and workflow thinking are inherently transferable. Designing and managing agents is a real skill, but our specific builder interface, connector logic, and knowledge graph are proprietary, so employees do build genuine AI skills just with a platform-specific dialect.',
    },
    {
      timestamp: 44,
      type: 'answer_question',
      questionId: 'learn-followup',
      answer:
        'Agent design and workflow thinking are inherently transferable. Designing and managing agents is a real skill, but our specific builder interface, connector logic, and knowledge graph are proprietary. Employees build genuine AI skills, just with a platform-specific dialect.',
    },
    {
      timestamp: 47,
      type: 'ai_note',
      note: {
        category: 'LEARN',
        content:
          '✅ Follow-up answered. Vendor clarified: agent design skills transfer, but their builder interface, connector logic, and knowledge graph are proprietary. Skills are "genuine but with a platform-specific dialect" — note the lock-in risk.',
      },
    },
  ],
};

// =============================================================================
// Q6 / EXIT Scenario
// =============================================================================

const EXIT_SCENARIO: DemoScenario = {
  id: 'exit',
  title: 'Data Portability',
  description: 'What happens to our data if we stop using Acme Corp?',
  criterion: 'EXIT',
  criterionName: 'Portability & Migration',
  videoSrc: '/demo/acme-q6-exit.mp4',
  liveQuestionId: 'exit-q1',
  questions: ACME_QUESTIONS_EXIT,
  categoryOrder: CATEGORY_ORDER,
  events: [
    {
      timestamp: 2,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: "What happens to my data, to my company's data, if we stop using Acme Corp's products?",
    },
    {
      timestamp: 11,
      type: 'transcript_line',
      speaker: 'Erica',
      text: "So, the index technically lives in the customer's virtual private cloud and stays there, but it's a living and breathing corpus, so without our software continuously updating and making sense of it, the value degrades pretty quickly.",
    },
    {
      timestamp: 18,
      type: 'answer_question',
      questionId: 'exit-q1',
      answer:
        "The index technically lives in the customer's virtual private cloud and stays there, but it's a living and breathing corpus, so without our software continuously updating and making sense of it, the value degrades pretty quickly.",
    },
    {
      timestamp: 19,
      type: 'ai_note',
      note: {
        category: 'EXIT',
        question: 'What specifically can you export and use elsewhere? What format?',
        content:
          'Vendor says data "lives in your VPC," but then admits value degrades quickly without their software.',
      },
    },
    {
      timestamp: 20,
      type: 'press_harder',
      criterion: 'EXIT',
      message:
        'The vendor says your index "lives in your VPC," but then admits value degrades quickly without their software. Ask what you can actually export and use elsewhere.',
    },
    {
      timestamp: 21,
      type: 'suggest_followup',
      criterion: 'EXIT',
      followUpQuestion:
        'What specifically could we export and use elsewhere? Can you tell me what format that would be in?',
      followUpContext:
        'The vendor framed VPC storage positively, but the data is effectively unusable without their platform. Get specifics on portability.',
    },
    {
      timestamp: 23,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: 'I have one follow-up question for that.',
    },
    {
      timestamp: 25,
      type: 'transcript_line',
      speaker: 'Erica',
      text: 'Sure.',
    },
    {
      timestamp: 26,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: 'What specifically could we export and use elsewhere, then? And can you tell me what format that would be in?',
    },
    {
      timestamp: 34,
      type: 'transcript_line',
      speaker: 'Erica',
      text: "So, your raw documents stay in your VPC and are fully portable. Now, the intelligence layer, like the enterprise map, the expertise maps, the behavioral signals, the topic graphs, that's proprietary and stays with Acme.",
    },
    {
      timestamp: 42,
      type: 'answer_question',
      questionId: 'exit-followup',
      answer:
        "Raw documents stay in VPC and are fully portable. The intelligence layer (enterprise map, expertise maps, behavioral signals, topic graphs) is proprietary and stays with Acme.",
    },
    {
      timestamp: 44,
      type: 'ai_note',
      note: {
        category: 'EXIT',
        content:
          '✅ Follow-up answered. Raw documents are portable, but the high-value intelligence layer (enterprise map, expertise maps, behavioral signals, topic graphs) is proprietary and non-exportable. Significant lock-in risk confirmed.',
      },
    },
    {
      timestamp: 45,
      type: 'transcript_line',
      speaker: 'Maddie',
      text: "Interesting, so all the valuable stuff. Is this an open platform, or more of a walled garden?",
    },
  ],
};

// =============================================================================
// Exports
// =============================================================================

export const DEMO_SCENARIOS: DemoScenario[] = [LEARN_SCENARIO, EXIT_SCENARIO];

export function getDemoScenario(id: string): DemoScenario | undefined {
  return DEMO_SCENARIOS.find((s) => s.id === id);
}
